import React from 'react';
import { Palette } from 'lucide-react';
import type { LogoPreviewProps } from './types';
import { clampLogoImageSizeForRender } from './useLogoUpload';

export const LogoPreview: React.FC<LogoPreviewProps> = ({ logoData, activeTab, hasImage }) => {
  const getTextSizeClass = () => {
    switch (logoData.textSize) {
      case 'small':
        return 'text-lg';
      case 'large':
        return 'text-2xl';
      case 'xl':
        return 'text-3xl';
      default:
        return 'text-xl';
    }
  };

  const getTextWeightClass = () => {
    const weightClasses: Record<string, string> = {
      light: 'font-light',
      normal: 'font-normal',
      bold: 'font-bold',
    };
    return weightClasses[logoData.textWeight || 'bold'] || 'font-bold';
  };

  const renderTextLogo = () => (
    <span
      className={`${getTextWeightClass()} ${getTextSizeClass()} truncate max-w-[200px]`}
      style={{ color: logoData.textColor || '#000000' }}
    >
      {logoData.text}
    </span>
  );

  const renderImageLogo = () => {
    const { width, height, maxWidth, maxHeight } = clampLogoImageSizeForRender(
      logoData.imageWidth,
      logoData.imageHeight,
      'image'
    );

    return (
      <img
        src={logoData.cloudfrontUrl || logoData.imageUrl}
        alt="Logo preview"
        className="object-contain"
        style={{
          width: `${width}px`,
          height: `${height}px`,
          maxWidth: `${maxWidth}px`,
          maxHeight: `${maxHeight}px`,
        }}
      />
    );
  };

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <h3 className="font-medium text-gray-900 mb-4 flex items-center space-x-2">
        <Palette size={18} />
        <span>Live Preview</span>
      </h3>

      <div className="bg-white rounded-lg shadow-sm p-4 border">
        <div className="flex items-center justify-between h-16 px-4 overflow-hidden">
          <div className="flex items-center min-w-0 max-h-14">
            {activeTab === 'text' && logoData.text && renderTextLogo()}
            {activeTab === 'image' && hasImage && renderImageLogo()}
            {!logoData.text && !hasImage && (
              <span className="text-gray-400 italic">Logo preview will appear here</span>
            )}
          </div>

          <div className="hidden md:flex items-center space-x-6 flex-shrink-0">
            <span className="text-gray-600 text-sm">Home</span>
            <span className="text-gray-600 text-sm">About</span>
            <span className="text-gray-600 text-sm">Contact</span>
          </div>
        </div>
      </div>

      <p className="mt-4 text-xs text-gray-500">
        This preview shows how your logo will appear in the navigation bar.
      </p>
    </div>
  );
};
